import { defineStore } from 'pinia';
import config from '@/config';
import i18n from '@/i18n';
import type { CodeSystemConcept, Coding, GroupCharacteristic } from 'fhir/r4';
import { ValidationTagValue } from '@/tsEnums/ValidationTagValue';

const { t } = i18n.global;

export const useFhirStore = defineStore('fhirStore', {
  state: () => {
    return {
      resourceInitializaitionDone: false, //Whether process of fetching ValueSets, ConceptMaps etc from FHIR server is done.
      resourceInitializationSuccessful: false,
      urls: {
        hiddenExtensionUrl: 'hidden',
        hiddenCommentExtensionUrl: 'comment',
        variantCodeSystemUrl: 'http://www.ncbi.nlm.nih.gov/clinvar',
        variantValueSetUrl: 'http://molit.eu/fhir/trials/ValueSet/clinvar-vs',
        contactOriginExtensionUrl:
          'http://molit.eu/fhir/trials/StructureDefinition/ContactOriginElementExtension',
        changeRequestTaskUrl:
          'http://molit.eu/fhir/trials/StructureDefinition/change-request-task',
        changeRequestTaskExtensionUrl:
          'http://molit.eu/fhir/trials/StructureDefinition/ChangeRequestTaskExtension',
        trialTitleExtension:
          'http://molit.eu/fhir/trials/StructureDefinition/TitleExtension', //For brief title
        contactOriginVsUrl:
          'http://molit.eu/fhir/trials/ValueSet/origin-identifier-vs',
        identifierVsUrl:
          'http://molit.eu/fhir/trials/ValueSet/trial-identifier-vs',
        statusCodeSystemUrl: 'http://hl7.org/fhir/CodeSystem/status',
        dataAbsentReasonCodeSystemUrl:
          'http://terminology.hl7.org/CodeSystem/data-absent-reason', //for site status unknown
        hiddenStructDefUrl:
          'http://molit.eu/fhir/trials/StructureDefinition/HiddenTrial', //used to be http://molit.eu/fhir/vitu/trials/StructureDefinition/HiddenTrial
        statusConceptMapUrl:
          'http://molit.eu/fhir/trials/ConceptMap/clinical-trials-to-fhir-researchstudy-status',
        ctStatusConceptMapUrl:
          'http://molit.eu/fhir/trials/ConceptMap/fhir-researchstudy-status-to-clinical-trials',
        tumorregionConceptMapUrl:
          'http://molit.eu/fhir/trials/ConceptMap/Tumorregion',
        tumorentityConceptMapUrl:
          'http://molit.eu/fhir/trials/ConceptMap/Tumorentity',
        solidTumorsConceptMapUrl:
          'http://molit.eu/fhir/trials/ConceptMap/SolidTumors',
        germanDistrictsCsUrl:
          'http://molit.eu/fhir/CodeSystem/DeutscheBundeslaender',
        uiccSystem: 'http://molit.eu/fhir/trials/CodeSystem/UICCCS',
        hgncSystem: 'http://www.genenames.org/geneId',
        hgncSystemWild: config.CHANGED_WILDTYPE_GENE_SYSTEM,
        treatmentLineSystem: 'http://molit.eu/trials/treatmentline',
        atcSystem: 'http://www.whocc.no/atc',
        ncitSystem: 'http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl',
        loincSystem: 'http://loinc.org',
        ctStatusCodeSystemUrl:
          'https://clinicaltrials.gov/data-api/about-api/study-data-structure#enum-Status',
        titleExtensionUrl:
          'http://molit.eu/fhir/trials/StructureDefinition/TitleExtension',
        groupStructDefUrl:
          'http://molit.eu/fhir/trials/StructureDefinition/study-inclusion-group',
        locationStructDefUrl:
          'http://molit.eu/fhir/trials/StructureDefinition/study-site-location',
        //Use this for facility status Extension
        facilityStatusStructDefUrl:
          'http://molit.eu/fhir/trials/StructureDefinition/ClinicalTrialsGovFacilityStatus',
        //Use this for study status Extension
        ctStatusStructDefUrl:
          'http://molit.eu/fhir/trials/StructureDefinition/ClinicalTrialsGovStatus',
        //Use this for human annotated study status
        humanClinicalTrialStatusUrl:
          'http://molit.eu/fhir/trials/StructureDefinition/HumanClinicalTrialsGovStatus',
        //Use this for human annotated facility status
        humanFacilityClinicalTrialStatusUrl:
          'http://molit.eu/fhir/trials/StructureDefinition/HumanClinicalTrialsGovFacilityStatus',
        researchStudyStructDefUrl:
          'http://molit.eu/fhir/trials/StructureDefinition/research-study',
        searchQueryStoreProfileUrl:
          'http://molit.eu/fhir/trials/StructureDefinition/search-query-store',
        basicResourceCodesCsUrl:
          'http://molit.eu/fhir/trials/CodeSystem/BasicResourceCodes',
        searchQueryExtensionUrl:
          'http://molit.eu/fhir/trials/StructureDefinition/SearchQueryExtension',
        searchQueryExtensionNameUrl: 'name',
        searchQueryExtensionQueryUrl: 'queryString',
        roleCodeCsUrl: 'http://terminology.hl7.org/CodeSystem/v3-RoleCode',
        icd10CsUrl: 'http://fhir.de/CodeSystem/bfarm/icd-10-gm',
        hgncVsUrl: 'http://molit.eu/fhir/ValueSet/HGNCGeneIdVS',
        treatmentLineVsUrl:
          'http://molit.eu/fhir/trials/ValueSet/treatment-line-vs',
        substancesVsUrl: 'http://molit.eu/fhir/ValueSet/substances-protocols',
        uiccVsUrl: 'http://molit.eu/fhir/trials/ValueSet/UiccStageVS',
        biomarkerVsUrl: 'http://molit.eu/fhir/trials/ValueSet/biomarker-vs',
        fhirStatusVsUrl:
          'http://molit.eu/fhir/trials/ValueSet/researchstudystatus-vs',
        //Don't use for study or facility status
        ctStatusVsUrl:
          'http://molit.eu/fhir/trials/ValueSet/clinical-trial-status',
        //Use for study status selection
        ctCoreStatusVsUrl:
          'http://molit.eu/fhir/trials/ValueSet/clinical-trial-status-core',
        //Use for site status selection
        catFacilityStatusVsUrl:
          'http://molit.eu/fhir/trials/ValueSet/facility-status',
        conditionVsUrl: 'http://molit.eu/fhir/ValueSet/icd-10-gm-c-II',
        subtypeVsUrl: 'http://molit.eu/fhir/trials/ValueSet/tumor-type-vs',
        cityVsUrl: 'http://molit.eu/fhir/trials/ValueSet/city-names',
        hmlVsUrl: 'http://molit.eu/fhir/trials/ValueSet/LL3578-3',
        regionVsUrl: 'http://molit.eu/fhir/trials/ValueSet/tumor-regions-vs',
        entityVsUrl: 'http://molit.eu/fhir/trials/ValueSet/tumor-entities-vs',
        auditExtensionUrls: {
          statusElementExtensionUrl:
            'http://molit.eu/fhir/trials/StructureDefinition/StatusElementExtension',
          subtypeAuditExtensionUrl:
            'http://molit.eu/fhir/trials/StructureDefinition/SubtypeElementExtension',
          uiccAuditExtensionUrl:
            'http://molit.eu/fhir/trials/StructureDefinition/UiccElementExtension',
          geneAuditExtensionUrl:
            'http://molit.eu/fhir/trials/StructureDefinition/GeneElementExtension',
          lineOfTreatmentAuditExtensionUrl:
            'http://molit.eu/fhir/trials/StructureDefinition/LineOfTreatmentElementExtension',
          pathwayAuditExtensionUrl:
            'http://molit.eu/fhir/trials/StructureDefinition/PathwayElementExtension',
          adjuvantAuditExtensionUrl:
            'http://molit.eu/fhir/trials/StructureDefinition/AdjuvantElementExtension',
          tmbAuditExtensionUrl:
            'http://molit.eu/fhir/trials/StructureDefinition/TmbElementExtension',
          conditionAuditExtensionUrl:
            'http://molit.eu/fhir/trials/StructureDefinition/ConditionElementExtension',
          substanceAuditExtensionUrl:
            'http://molit.eu/fhir/trials/StructureDefinition/SubstanceElementExtension',
          genericAuditExtensionUrl:
            'http://molit.eu/fhir/trials/StructureDefinition/GenericAuditElementExtension',
        },
      },
      valueSets: {
        contactOriginValueSet: [] as Array<{
          code: string;
          display: string;
        }>,
        substancesValueSet: [] as Array<{
          code: string;
          display: string;
        }>,
        subtypeValueSet: [] as Array<{
          code: string;
          display: string;
        }>,
        conditionValueSet: [] as Array<{
          code: string;
          display: string;
        }>,
        conditionValueSetWithSpecificAndGeneralInfoSeparated: [] as Array<{
          code: string;
          display: string;
          icdGeneral: string;
          icdSpecific: string;
        }>, //For easier display, e.g. in v-selects
        cityValueSet: [] as Array<{
          code: string;
          display: string;
        }>,
        treatmentLineValueSet: [] as Array<{
          code: string;
          display: string;
        }>,
        hmlValueSet: [] as Array<{
          code: string;
          display: string;
        }>,
        entityValueSet: [] as Array<{
          code: string;
          display: string;
        }>,
        regionValueSet: [] as Array<{
          code: string;
          display: string;
        }>,
        biomarkerValueSet: [] as Array<{
          code: string;
          display: string;
        }>,
        uiccValueSet: [] as Array<{
          code: string;
          display: string;
        }>,
        hgncValueSet: [] as Array<{
          code: string;
          display: string;
        }>,
        identifierValueSet: [] as Array<{
          system: string;
          code: string;
          display: string;
        }>,
        variantValueSet: [] as Array<{
          system: string;
          code: string;
          display: string;
        }>,
        facilityStatusValueSet: [] as Array<{
          code: string;
          display: string;
          system: string;
        }>,
        ctCoreStatusValueSet: [] as Array<{
          code: string;
          display: string;
        }>,
      },
      codeSystems: {
        ctStatusCodeSystem: [] as Array<CodeSystemConcept>,
        germanDistrictsCs: [] as Array<{
          code: string;
          display: string;
        }>,
      },
      validationStatusTags: [
        { code: '', display: 'Alle' },
        { code: ValidationTagValue.Validated, display: 'Validiert' },
        { code: ValidationTagValue.InProcess, display: 'Nicht Validiert' },
        {
          code: ValidationTagValue.ReqRevalid,
          display: 'Revalidierung notwendig',
        },
      ], //Note: Not the official display values.
      loincAnswers: [
        {
          code: 'LA6576-8',
          display: 'Positive',
          system: 'https://loinc.org/LL3639-3/',
        },
        {
          code: 'LA6577-6',
          display: 'Negative',
          system: 'https://loinc.org/LL3639-3/',
        },
        {
          code: 'LA4489-6',
          display: 'Unknown',
          system: 'https://loinc.org/LL3639-3/',
        },
        {
          code: 'LA13538-6',
          display: 'Not tested',
          system: 'https://loinc.org/LL3639-3/',
        },
      ] as Array<Coding>,
      ctConceptMap: [],
      ctStatusValues: [] as Array<{ code: string; display: string }>,
      validFhirStatusValues: [] as Array<{ code: string; display: string }>,
      groupCharacteristicNcitCodes: {
        //NCIT codes for Group.characteristic in one place
        condition: 'C154625',
        substance: 'C45306',
        tmb: '94076-7',
        adjuvant: 'C2140',
        pathway: 'C54214',
        lineOfTreatment: 'C133518',
        mutatedGene: 'C18093',
        wildtypeGene: 'C16612',
        uicc: 'C39434',
        subtype: 'C25696',
        biomarker: '',
        variant: '81252-9', //not really an nci code, but loinc. But is used for GroupCharacteristic code like the others.
      },
      groupCharacteristicDisplayValues: {
        //Display values for Group.characteristic in one place
        condition: 'Condition',
        substance: 'Substance',
        tmb: 'Tumor Mutational Burden',
        adjuvant: 'Adjuvant',
        pathway: 'Pathway',
        lineOfTreatment: 'Line of Therapy',
        mutatedGene: 'Gene Mutated',
        wildtypeGene: 'Gene Wildtype',
        uicc: 'UICC',
        subtype: 'Subtype',
        biomarker: 'Biomarker',
        variant: 'Variant',
      },
      tumorcategoryOptions: [
        //Array to hold tumor categories. To be used as long as no ValueSet exists for those.
        { code: 'solidTumor', display: t('tumorcategories.solidTumors') },
      ] as Array<{ code: string; display: string }>,
      includeExcludeOptions: [
        { code: 'include', display: 'Include', label: t('inclusion') },
        { code: 'exclude', display: 'Exclude', label: t('exclusion') },
      ],
      basicResourceSearchQueryCode: 'SEARCH_QUERY_STORE',
      basicResourceSearchQueryDisplay: 'Search query store',
    };
  },
  getters: {
    //Returns viable options for the adjuvant characteristic for use in filter.
    adjuvantOptions: (state): GroupCharacteristic[] => {
      return [
        {
          code: {
            coding: [
              {
                system: state.urls.ncitSystem,
                code: 'C2140',
              },
            ],
          },
          valueBoolean: true,
          exclude: false,
        },
        {
          code: {
            coding: [
              {
                system: state.urls.ncitSystem,
                code: 'C2140',
              },
            ],
          },
          valueBoolean: false,
          exclude: false,
        },
      ];
    },
  },
  actions: {
    /**
     * Maps NCIT codes (specifying group:characteristic categories) to the urls for the corresponding audit element extensions
     * @param attributeCode NCIT code for characteristic category, e.g. C25696 for Subtype
     */
    getAuditUrl(attributeCode) {
      console.log('getAuditUrl called. Attribute code: ' + attributeCode);
      switch (attributeCode) {
        case this.groupCharacteristicNcitCodes.subtype: //Subtype
          return this.urls.auditExtensionUrls.subtypeAuditExtensionUrl;
        case this.groupCharacteristicNcitCodes.uicc: //UICC
          return this.urls.auditExtensionUrls.uiccAuditExtensionUrl;
        case this.groupCharacteristicNcitCodes.wildtypeGene: //Wildtype Gene
          return this.urls.auditExtensionUrls.geneAuditExtensionUrl;
        case this.groupCharacteristicNcitCodes.mutatedGene: //mutated Gene
          return this.urls.auditExtensionUrls.geneAuditExtensionUrl;
        case this.groupCharacteristicNcitCodes.lineOfTreatment: //line of treatment
          return this.urls.auditExtensionUrls.lineOfTreatmentAuditExtensionUrl;
        case this.groupCharacteristicNcitCodes.pathway: //pathway
          return this.urls.auditExtensionUrls.pathwayAuditExtensionUrl;
        case this.groupCharacteristicNcitCodes.adjuvant: //adjuvant
          return this.urls.auditExtensionUrls.adjuvantAuditExtensionUrl;
        case this.groupCharacteristicNcitCodes.tmb: //tmb
          return this.urls.auditExtensionUrls.tmbAuditExtensionUrl;
        case this.groupCharacteristicNcitCodes.condition: //icd
          return this.urls.auditExtensionUrls.conditionAuditExtensionUrl;
        case this.groupCharacteristicNcitCodes.substance: //Drug
          return this.urls.auditExtensionUrls.substanceAuditExtensionUrl;
        default:
          return this.urls.auditExtensionUrls.genericAuditExtensionUrl; //Biomarker and Variants fall under default. TODO is that really the desired option?
      }
    },
  },
});
